import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { NgSelectModule } from '@ng-select/ng-select';
import { HttpClient } from '@angular/common/http';
import { finalize } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';

export interface SelectNewTourDialogData {
    warehouseCode?: string | null;
    comParcelsList?: number[];
}

export interface TourRouting {
    tourRoutingId: number;
    tourRoutingCode: string;
    carriers?: CarrierResponse[] | null;
}

export interface CarrierResponse {
    carrierCode: string;
    carrierName: string;
    defaultCarrier?: boolean;
}

export function searchCarrierFn(term: string, item: CarrierResponse): boolean {
    const t = term.toLowerCase();
    return (
        (item.carrierName || '').toLowerCase().includes(t) ||
        (item.carrierCode || '').toLowerCase().includes(t)
    );
}

@Component({
    selector: 'app-select-new-tour-dialog',
    standalone: true,
    templateUrl: './select-new-tour-dialog.component.html',
    styleUrls: ['./select-new-tour-dialog.component.css'],
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatButtonModule,
        NgSelectModule
    ]
})
export class SelectNewTourDialogComponent {
    loading = false;
    error: string | null = null;

    tours: TourRouting[] = [];

    carriers: CarrierResponse[] = [];
    private globalCarriers: CarrierResponse[] = [];

    tourCtrl = new FormControl<TourRouting | null>(null, {
        validators: [Validators.required]
    });

    carrierCtrl = new FormControl<string | null>(null);
    loadingCarriers = false;
    private carriersRequestId = 0;

    constructor(
        private http: HttpClient,
        private dialogRef: MatDialogRef<SelectNewTourDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: SelectNewTourDialogData
    ) {
        // Update carriers list when tour changes
        this.tourCtrl.valueChanges.subscribe((t) => {
            const list = this.extractCarriersFromTour(t) ?? this.globalCarriers;
            this.carriers = Array.isArray(list) ? list : [];
            // reset previous selection
            this.carrierCtrl.setValue(null);
            // auto-select when there is only one option
            if (this.carriers.length === 1) {
                this.carrierCtrl.setValue(this.carriers[0].carrierCode);
            }
            this.loadDefaultCarrier(t);
        });

        this.fetchTours();
    }

    fetchTours(): void {
        this.loading = true;
        this.error = null;

        const url = `${environment.apiUrl}/change-tour/tours`;
        const params: Record<string, string> = {};

        if (this.data?.warehouseCode) {
            params['warehouseCode'] = this.data.warehouseCode;
        }

        this.http.get<any>(url, { params }).subscribe({
            next: (res) => {
                if (Array.isArray(res)) {
                    this.tours = res as TourRouting[];
                } else if (res && typeof res === 'object') {
                    if (Array.isArray(res.tours)) {
                        this.tours = res.tours as TourRouting[];
                    } else if (Array.isArray(res.data)) {
                        this.tours = res.data as TourRouting[];
                    } else {
                        this.tours = [];
                    }

                    // extract optional global carriers from various possible envelope shapes
                    this.globalCarriers = this.extractCarriers(res);
                } else {
                    this.tours = [];
                    this.globalCarriers = [];
                }

                this.loading = false;
            },
            error: (err) => {
                this.error = err?.error?.message || 'Errore nel caricamento dei tour';
                this.tours = [];
                this.carriers = [];
                this.globalCarriers = [];
                this.loading = false;
            }
        });
    }

    private loadDefaultCarrier(tour: TourRouting | null): void {
        const requestId = ++this.carriersRequestId;
        if (!tour || this.carriers.length <= 1) {
            this.loadingCarriers = false;
            return;
        }
        this.loadingCarriers = true;
        const payload = {
            comParcelsList: this.data?.comParcelsList || [],
            warehouseCode: this.data?.warehouseCode || null,
            tourRoutingId: tour.tourRoutingId
        };
        this.http.post<{ carriers: CarrierResponse[] }>(`${environment.apiUrl}/change-tour/tours/carriers`, payload)
            .pipe(finalize(() => {
                if (requestId === this.carriersRequestId) this.loadingCarriers = false;
            }))
            .subscribe({
                next: (response) => {
                    if (requestId !== this.carriersRequestId) return;
                    const defaultCarrier = (response?.carriers || []).find(c => c.defaultCarrier);
                    if (defaultCarrier && this.carriers.some(c => c.carrierCode === defaultCarrier.carrierCode)) {
                        this.carrierCtrl.setValue(defaultCarrier.carrierCode);
                    }
                },
                error: () => {}
            });
    }

    searchCarrierFn = searchCarrierFn;

    getDisplay(t: TourRouting | null): string {
        if (!t) return '';
        return t.tourRoutingCode ?? '';
    }

    cancel(): void {
        this.dialogRef.close(null);
    }

    confirm(): void {
        if (this.tourCtrl.invalid || !this.tourCtrl.value) return;
        if (this.carriers.length > 0 && !this.carrierCtrl.value) return;

        this.dialogRef.close({
            tourRoutingId: this.tourCtrl.value.tourRoutingId,
            carrierCode: this.carrierCtrl.value || null
        });
    }

    // ----- helpers to normalize carriers from different backend shapes -----
    private toCarrierResponseList(arr: any[]): CarrierResponse[] {
        if (!Array.isArray(arr)) return [];
        return arr
            .map((c) => {
                const code = (c?.carrierCode ?? c?.code ?? c?.carrier_id ?? c?.id ?? '').toString();
                const name = (c?.carrierName ?? c?.name ?? c?.description ?? '').toString();
                if (!code) return null;
                return { carrierCode: code, carrierName: name } as CarrierResponse;
            })
            .filter((x): x is CarrierResponse => !!x);
    }

    private extractCarriers(obj: any): CarrierResponse[] {
        if (!obj || typeof obj !== 'object') return [];

        const candidates: any[] = [];
        if (Array.isArray(obj.carriers)) candidates.push(obj.carriers);
        if (obj.data && Array.isArray(obj.data.carriers)) candidates.push(obj.data.carriers);
        if (obj.carrierListResponse && Array.isArray(obj.carrierListResponse.carriers)) candidates.push(obj.carrierListResponse.carriers);
        if (Array.isArray(obj.carriersList)) candidates.push(obj.carriersList);

        if (candidates.length > 0) {
            return this.toCarrierResponseList(candidates[0]);
        }
        // Fallback: try to find any property that looks like carriers (case-insensitive)
        try {
            const lowerKeyMap: Record<string, any> = {};
            Object.keys(obj).forEach(k => lowerKeyMap[k.toLowerCase()] = (obj as any)[k]);

            // top-level array property whose name contains 'carrier'
            for (const k of Object.keys(lowerKeyMap)) {
                const v = lowerKeyMap[k];
                if (k.includes('carrier') && Array.isArray(v)) {
                    return this.toCarrierResponseList(v);
                }
            }

            // nested object having array property 'carriers'
            for (const k of Object.keys(lowerKeyMap)) {
                const v = lowerKeyMap[k];
                if (v && typeof v === 'object') {
                    const inner = this.extractCarriers(v);
                    if (inner.length > 0) return inner;
                }
            }
        } catch { /* ignore */ }

        return [];
    }

    private extractCarriersFromTour(t: TourRouting | null): CarrierResponse[] | null {
        if (!t) return null;
        const anyTour: any = t as any;

        if (Array.isArray(anyTour.carriers)) {
            return this.toCarrierResponseList(anyTour.carriers);
        }

        if (anyTour.carrierListResponse && Array.isArray(anyTour.carrierListResponse.carriers)) {
            return this.toCarrierResponseList(anyTour.carrierListResponse.carriers);
        }

        if (Array.isArray(anyTour.carrierResponses)) {
            return this.toCarrierResponseList(anyTour.carrierResponses);
        }

        if (anyTour.carrierList && Array.isArray(anyTour.carrierList.carriers)) {
            return this.toCarrierResponseList(anyTour.carrierList.carriers);
        }
        // Generic fallbacks (case-insensitive scan on the tour object too)
        try {
            const lowerKeyMap: Record<string, any> = {};
            Object.keys(anyTour).forEach(k => lowerKeyMap[k.toLowerCase()] = anyTour[k]);

            for (const k of Object.keys(lowerKeyMap)) {
                const v = lowerKeyMap[k];
                if (k.includes('carrier') && Array.isArray(v)) {
                    return this.toCarrierResponseList(v);
                }
                if (v && typeof v === 'object' && Array.isArray(v.carriers)) {
                    return this.toCarrierResponseList(v.carriers);
                }
            }
        } catch { /* ignore */ }

        return null;
    }
}